import * as React from "react";

import { OverflowNotifierProps } from "./OverflowNotifier.types";

/**
 * OverflowNotifier monitors the width of its children and calls onOverflow when the children get wider than the window.
 * It is given scaling steps it can apply by dispatching store updates, but internally it's being updated by
 * registering ResizeObservables on its containers and then responding with scaling accordingly.
 *
 * ResizeObservables and width: min-content are both currently unsupported in IE11, but work in all other browsers.
 */
export const OverflowNotifier: React.FunctionComponent<OverflowNotifierProps> =
  React.memo(({ children, onOverflow, onError }) => {
    const contentRef: React.RefObject<HTMLDivElement> = React.useRef(null);
    const haveNotifiedCurrentOverflow = React.useRef(false);
    const intersectionObserver = React.useRef<IntersectionObserver>();
    const resizeObserver = React.useRef<ResizeObserver>();

    /**
     * Callback when inner element overflows the window width. This will happen when the inner element
     * changes size (e.g. via window resize), where the resulting width makes the inner element overflow.
     * This will not be called when elements are added/changed and that causes an overflow because it will
     * be handled by onContentResizeObserved and the rescale callback should force rerender.
     * If the callback for onContentResizeObserved does not fix the overflow issue, this function will be called.
     *
     * Note: This function is called after the next frame (vs before the next frame for onContentResizeObserver)
     * which could cause shifting if the callback in onContentResizeObserver did not make the content not overflow.
     */
    const onContentIntersectionObserved = React.useCallback(
      (entries: IntersectionObserverEntry[]) => {
        try {
          if (entries.length !== 1) {
            // this should never happen
            return;
          }

          const intersectionRatio = entries[0].intersectionRatio;

          // If we have already notified that we are overflowing,
          // do not fire the callback again
          if (haveNotifiedCurrentOverflow.current) {
            // If we get here and the aspect ratio is 1,
            // we know are going from overflowing to no longer overflowing.
            if (intersectionRatio === 1) {
              haveNotifiedCurrentOverflow.current = false;
            }

            return;
          }

          // If we get here and the intersection ratio is less than one,
          // we know that we are overflowing and need to fire the callback
          if (intersectionRatio < 1) {
            haveNotifiedCurrentOverflow.current = true;
            onOverflow();
          }
        } catch (exception: any) {
          const message =
            exception instanceof Error
              ? exception.message
              : typeof exception === "string"
              ? exception
              : "unknown";
          onError?.(
            `Error in OverflowNotifier.onContentIntersectionObserved with message: ${message}`
          );
        }
      },
      [onError, onOverflow]
    );

    /**
     * Callback when inner element's width changes. This will happen when some child element inside
     * decides to change its dimensions, perhaps as a result of different render output for the children of this resize group.
     *
     * Note: this callback is called before the next frame (vs after the next frame for onContentIntersectionObserved).
     * If the rescale callback does not fix the overflow onContentIntersectionObserved will be called in the next frame.
     */
    const onContentResizeObserved = React.useCallback(
      (entries: ResizeObserverEntry[]) => {
        try {
          if (entries.length !== 1) {
            // this should never happen
            return;
          }

          // Get the width and round up to the next whole px for safety
          const contentsWidth = Math.ceil(entries[0].contentRect.width || -1);

          // If contents are wider (overflowing), fire the callback
          if (contentsWidth - window.innerWidth > 0) {
            onOverflow();
          }
        } catch (exception: any) {
          const message =
            exception instanceof Error
              ? exception.message
              : typeof exception === "string"
              ? exception
              : "unknown";
          onError?.(
            `Error in OverflowNotifier.onContentResizeObserved with message: ${message}`
          );
        }
      },
      [onError, onOverflow]
    );

    // componentDidMount/Update
    React.useEffect(() => {
      try {
        // If the functions changed, disconnect the existing observers
        intersectionObserver.current?.disconnect();
        resizeObserver.current?.disconnect();

        intersectionObserver.current = new IntersectionObserver(
          onContentIntersectionObserved,
          {
            threshold: 1 /* ratio of intersection */,
          }
        );
        resizeObserver.current = new ResizeObserver(onContentResizeObserved);

        if (contentRef.current) {
          intersectionObserver.current.observe(contentRef.current);
          resizeObserver.current.observe(contentRef.current);
        }
      } catch (exception: any) {
        const message =
          exception instanceof Error
            ? exception.message
            : typeof exception === "string"
            ? exception
            : "unknown";
        onError?.(
          `Error in OverflowNotifier.useEffect with message: ${message}`
        );
      }

      // componentWillUnmount
      return () => {
        try {
          intersectionObserver.current?.disconnect();
          resizeObserver.current?.disconnect();
        } catch (exception: any) {
          const message =
            exception instanceof Error
              ? exception.message
              : typeof exception === "string"
              ? exception
              : "unknown";
          onError?.(
            `Error in OverflowNotifier.useEffect unmount with message: ${message}`
          );
        }
      };
    }, [onError, onContentIntersectionObserved, onContentResizeObserved]);

    return (
      <div
        ref={contentRef}
        style={{
          display: "inline-block",
          position: "relative",
          whiteSpace: "nowrap",
          width: "min-content",
        }}
      >
        {children}
      </div>
    );
  });

OverflowNotifier.displayName = "OverflowNotifier";
